import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RestaurantPreparationStationDTO,
  RestaurantPreparationStationForm,
  RestaurantStationAssignmentDTO,
  RestaurantStationAssignmentForm,
  RestaurantStationUserOption,
} from '../../../types/restaurant/RestaurantPreparationStationTypes';
import { RestaurantPrinterDTO } from '../../../types/restaurant/RestaurantPrinterTypes';
import { PageMetadata } from '../../../types/restaurant/PagedResponse';

export const emptyStationForm: RestaurantPreparationStationForm = {
  name: '', description: '', printerId: '', displayOrder: 1,
  estimatedPreparationMinutes: 15, enabled: true,
};
const emptyAssignmentForm: RestaurantStationAssignmentForm = {
  userId: '', primaryAssignment: false,
};

interface State {
  list: RestaurantPreparationStationDTO[];
  page: PageMetadata | null;
  printers: RestaurantPrinterDTO[];
  assignments: RestaurantStationAssignmentDTO[];
  users: RestaurantStationUserOption[];
  form: RestaurantPreparationStationForm;
  assignmentForm: RestaurantStationAssignmentForm;
  selectedStation: RestaurantPreparationStationDTO | null;
  loading: boolean;
  saving: boolean;
  stationModalOpen: boolean;
  assignmentModalOpen: boolean;
}

const initialState: State = {
  list: [], page: null, printers: [], assignments: [], users: [],
  form: emptyStationForm, assignmentForm: emptyAssignmentForm,
  selectedStation: null, loading: false, saving: false,
  stationModalOpen: false, assignmentModalOpen: false,
};

const slice = createSlice({
  name: 'restaurantPreparationStation',
  initialState,
  reducers: {
    setStations(state, action: PayloadAction<{ content: RestaurantPreparationStationDTO[]; page: PageMetadata }>) { state.list = action.payload.content; state.page = action.payload.page; },
    setStationPrinters(state, action: PayloadAction<RestaurantPrinterDTO[]>) { state.printers = action.payload; },
    setStationAssignments(state, action: PayloadAction<RestaurantStationAssignmentDTO[]>) { state.assignments = action.payload; },
    setStationUsers(state, action: PayloadAction<RestaurantStationUserOption[]>) { state.users = action.payload; },
    setStationLoading(state, action: PayloadAction<boolean>) { state.loading = action.payload; },
    setStationSaving(state, action: PayloadAction<boolean>) { state.saving = action.payload; },
    openStationCreate(state) { state.form = { ...emptyStationForm }; state.stationModalOpen = true; },
    openStationEdit(state, action: PayloadAction<RestaurantPreparationStationDTO>) {
      const item = action.payload;
      state.form = {
        id: item.id, code: item.code, name: item.name,
        description: item.description ?? '', printerId: item.printerId ?? '',
        displayOrder: item.displayOrder,
        estimatedPreparationMinutes: item.estimatedPreparationMinutes ?? '',
        enabled: item.enabled, active: item.active,
      };
      state.stationModalOpen = true;
    },
    closeStationModal(state) { state.stationModalOpen = false; state.form = { ...emptyStationForm }; },
    updateStationField(state, action: PayloadAction<{ field: keyof RestaurantPreparationStationForm; value: any }>) {
      state.form = { ...state.form, [action.payload.field]: action.payload.value };
    },
    openAssignments(state, action: PayloadAction<RestaurantPreparationStationDTO>) {
      state.selectedStation = action.payload; state.assignments = [];
      state.assignmentForm = { ...emptyAssignmentForm }; state.assignmentModalOpen = true;
    },
    closeAssignments(state) {
      state.assignmentModalOpen = false; state.selectedStation = null;
      state.assignments = []; state.assignmentForm = { ...emptyAssignmentForm };
    },
    updateAssignmentField(state, action: PayloadAction<{ field: keyof RestaurantStationAssignmentForm; value: any }>) {
      state.assignmentForm = { ...state.assignmentForm, [action.payload.field]: action.payload.value };
    },
  },
});

export const {
  setStations, setStationPrinters, setStationAssignments, setStationUsers,
  setStationLoading, setStationSaving, openStationCreate, openStationEdit,
  closeStationModal, updateStationField, openAssignments, closeAssignments,
  updateAssignmentField,
} = slice.actions;
export default slice.reducer;
